/** A league at a glance: where it's up to, your part in it, and what's happened lately */
import { getBudgetLeft, getMatch, getOpponent, getPicker, getPointsUsed } from '@ogreclan/league-core';
import { useEffect, useState } from 'preact/hooks';
import { api, type LeagueEvent } from '../api';
import type { LeagueProps } from '../pages/league';
import { Link } from '../router';
import { ago, Countdown } from '../ui';
import { describeEvent, playerName, you as findYou } from './common';

export function OverviewTab({ view }: LeagueProps) {
	const { league, roomid } = view;
	const { settings } = league;
	const me = findYou(league, view.you);
	const picker = league.phase === 'draft' ? getPicker(league) : null;
	const [events, setEvents] = useState<LeagueEvent[] | null>(null);
	useEffect(() => {
		api.get<{ events: LeagueEvent[] }>(`/api/leagues/${roomid}/history`).then(result => setEvents(result.events), () => setEvents([]));
	}, [roomid, view.revision]);

	const match = me && league.phase === 'season' ? getMatch(league, league.week, me.id) : null;
	const opponentId = match ? getOpponent(match, me!.id) : null;

	return <div class="grid" style="grid-template-columns:repeat(auto-fit,minmax(320px,1fr))">
		<div>
			{league.phase === 'draft' && <div class={`clock ${picker && picker.id === me?.id ? 'yours' : ''}`}>
				<span class="big">{picker ? picker.id === me?.id ? `It's your pick!` : `${picker.name} is picking` : 'All the turns are done'}</span>
				{picker && league.pickDeadline && <span>(<Countdown until={league.pickDeadline} /> left)</span>}
				<Link class="button small" href={`/league/${roomid}/draft`}>Go to the draft</Link>
			</div>}
			{me && <div class="card">
				<h2>You</h2>
				<p>{me.roster.length} of {settings.picks} Pokémon{settings.type === 'points' &&
					<> · {getPointsUsed(league, me)} points used, {getBudgetLeft(league, me)} left</>}</p>
				{me.owedPicks > 0 && <p class="notice">You're owed {me.owedPicks} pick{me.owedPicks === 1 ? '' : 's'} from skipped turns:
					make {me.owedPicks === 1 ? 'it' : 'them'} on the <Link href={`/league/${roomid}/draft`}>draft page</Link>.</p>}
				{league.phase === 'season' && <p>{match ? opponentId ? <>Week {league.week}: you play{' '}
					<strong>{playerName(league, opponentId)}</strong>{match.result ? ' (played)' : ''}.</> : <>Week {league.week}: you have a bye.</> : null}
				{' '}<Link href={`/league/${roomid}/teams`}>Your teams</Link> · <Link href={`/league/${roomid}/trades`}>Trades</Link></p>}
				{league.phase === 'season' && <p class="muted small">Matches are played on Showdown: open the league's room on
					play.ogreclan.com and press Ready on your match.</p>}
			</div>}
			<div class="card">
				<h2>Rules</h2>
				<table><tbody>
					<tr><th>Format</th><td><code>{settings.format}</code></td></tr>
					<tr><th>Draft</th><td>{settings.type === 'points' ? `Points, ${settings.budget} to spend` : 'Free'}, {settings.picks} Pokémon
						each{settings.snake ? ', snake order' : ''}</td></tr>
					<tr><th>Pick timer</th><td>{settings.pickTimer ? `${settings.pickTimer} minutes` : 'None'}</td></tr>
					<tr><th>Tera Captains</th><td>{settings.tera ? `${settings.teraCaptains} each${settings.teraCaptainMaxPoints ?
						`, costing at most ${settings.teraCaptainMaxPoints}` : ''}` : 'No (Terastallization is whatever the format allows)'}</td></tr>
					<tr><th>Season</th><td>{settings.weeks} weeks, best of {settings.bestOf}</td></tr>
					<tr><th>Trades</th><td>{settings.trades} each, and {settings.freeAgents} free agent pickups</td></tr>
				</tbody></table>
			</div>
		</div>
		<div>
			<div class="card">
				<h2>Players</h2>
				<ol>{league.players.map(p => <li key={p.id}>{p.id === me?.id ? <strong>{p.name}</strong> : p.name}</li>)}</ol>
				{!league.players.length && <p class="empty">No players yet.</p>}
			</div>
			<div class="card">
				<h2>Lately</h2>
				{!events ? <p class="muted">Loading…</p> : !events.length ? <p class="empty">Nothing yet.</p> : <ul class="history">
					{events.slice(0, 15).map(e => <li key={e.id}>{describeEvent(league, e)}{' '}
						<span class="muted small">{ago(e.createdAt)}{e.source === 'web' ? ' · on the site' : ''}</span></li>)}
				</ul>}
				{league.transactions.length > 0 && <>
					<h3>Trades and pickups</h3>
					<ul class="history">{league.transactions.slice(-8).reverse().map((text, i) => <li key={i}>{text}</li>)}</ul>
				</>}
			</div>
		</div>
	</div>;
}
